import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import ContactSection from '../../components/feature/ContactSection';
import HubSpotForm from '../../components/common/HubSpotForm';
import { api } from '../../lib/api';
import { useContentManager } from '../../hooks/useContentManager';

type UnitTab = 'Studio' | '1 Bedroom' | '2 Bedroom' | 'Penthouse';
type ProjectTab = 'floorplan' | 'typical' | '3dview';

export default function NewReleasesPage() {
  // ---- Unit type (Studio / 1+1 / 2+1 / Penthouse) ----
  const [activeUnitTab, setActiveUnitTab] = useState<UnitTab>('Penthouse');

  // ---- Project content tabs (Location / Gallery / Floor Plans / Brochure) ----
  const [activeProjectTab, setActiveProjectTab] = useState<ProjectTab>('floorplan');

  // Admin content
  const { getContentItem } = useContentManager();
  
  // Get virtual tour setting from admin panel
  const virtualTourItem = getContentItem('residences', 'virtual-tour');
  const showVirtualTour = virtualTourItem?.metadata?.show !== false; // Default to true

  const [selectedTab, setSelectedTab] = useState<'Studio' | '1 Bedroom' | '2 Bedroom' | 'Penthouse'>('Penthouse');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);

  const [activeUnitTabState, setActiveUnitTabState] = useState('penthouse');
  const [currentSlide1, setCurrentSlide1] = useState(0);
  const [currentSlide2, setCurrentSlide2] = useState(0);
  const [currentSlide3, setCurrentSlide3] = useState(0);
  const [currentSlide4, setCurrentSlide4] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
  const [currentView3DSlide, setCurrentView3DSlide] = useState(0);
  const [currentFloorplanSlide, setCurrentFloorplanSlide] = useState(0);
  const [currentTypicalSlide, setCurrentTypicalSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    unitType: ''
  });
  const [countryCode, setCountryCode] = useState('+233');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const unitTabs = [
    { id: 'studio', label: 'Studio' },
    { id: '1bedroom', label: '1 Bedroom' },
    { id: '2bedroom', label: '2 Bedroom' },
    { id: 'penthouse', label: 'Penthouse' }
  ];

  // (Removed defaultSlides; sections are shown only if images are provided by admin)

  const unitKey =
    activeUnitTab === 'Studio'
      ? 'studio'
      : activeUnitTab === '1 Bedroom'
      ? '1bedroom'
      : activeUnitTab === '2 Bedroom'
      ? '2bedroom'
      : 'penthouse';

  // Hero content per unit
  const heroItem = getContentItem('residences', `${unitKey}-hero`);
  const heroImageUrl =
    (heroItem?.image_url as string | undefined) ||
    (heroItem?.metadata?.image_url as string | undefined) ||
    '';
  const heroVideoUrl = (heroItem?.metadata?.video_url as string | undefined);
  const heroPosterUrl = (heroItem?.metadata?.poster_url as string | undefined) || heroImageUrl;

  // Page-wide Hero Text (admin-managed)
  const residencesHeroText = getContentItem('residences', 'hero-text');
  const residencesHeroTitleLine1 = residencesHeroText?.metadata?.title_line1 || 'RESIDENCES';
  const residencesHeroTitleBold = residencesHeroText?.metadata?.title_bold || '';
  const residencesHeroSubtitle = residencesHeroText?.metadata?.subtitle || '';
  const residencesHeroButtonText = residencesHeroText?.metadata?.button_text as string | undefined;
  const residencesHeroButtonUrl = residencesHeroText?.metadata?.button_url as string | undefined;

  // Intro section (WHY) per unit
  const introItem = getContentItem('residences', `${unitKey}-intro`);
  const introTitle = introItem?.metadata?.title || 'WHY';
  const introHighlight = introItem?.metadata?.highlight || 'GOLDEN TOWER?';
  const introDescription =
    introItem?.metadata?.description ||
    'Golden Tower, delve deeper into the design philosophy behind Circle, where every element is carefully considered to create a poised way of life, truly embracing its name. Discover residences crafted with exquisite materials, each a testament to Golden Tower\'s unwavering pursuit of perfection.';
  const introButtonText = introItem?.metadata?.button_text || 'REGISTER YOUR INTEREST';
  const introButtonUrl = introItem?.metadata?.button_url as string | undefined;

  // Section contents per unit (metadata.images: string[] | { image|url|src, text|caption }[])
  const sec1 = getContentItem('residences', `${unitKey}-section1`);
  const sec2 = getContentItem('residences', `${unitKey}-section2`);
  const sec3 = getContentItem('residences', `${unitKey}-section3`);
  const sec4 = getContentItem('residences', `${unitKey}-section4`);

  // Footer content (page-wide for Residences)
  const footerItem = getContentItem('residences', 'footer');
  const footerBgUrl =
    (footerItem?.image_url as string | undefined) ||
    (footerItem?.metadata?.bg_image_url as string | undefined) ||
    '';
  const footerLeftTitleLine1 = footerItem?.metadata?.left_title_line1 || 'Connect with';
  const footerLeftTitleBold = footerItem?.metadata?.left_title_bold || 'the brand';
  const footerLeftLink1Text = footerItem?.metadata?.left_link1_text || 'FIND A SALES BOUTIQUE';
  const footerLeftLink1Url = footerItem?.metadata?.left_link1_url || '#';
  const footerLeftLink2Text = footerItem?.metadata?.left_link2_text || 'DISCOVER THE COLLECTION';
  const footerLeftLink2Url = footerItem?.metadata?.left_link2_url || '#';
  const footerFormTitleLine1 = footerItem?.metadata?.form_title_line1 || 'Express';
  const footerFormTitleBold = footerItem?.metadata?.form_title_bold || 'Your Interest';
  const footerSubmitButtonText = footerItem?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR';
  const footerTermsText = footerItem?.metadata?.terms_text || 'By submitting, you agree to our terms & conditions*';

  // Floor Plan (gallery-style with single-image fallback)
  const floorplanItem = getContentItem('residences', `${unitKey}-floorplan`);
  const floorplanFlat = (() => {
    const result: { image: string }[] = [];
    const list = floorplanItem?.metadata?.images;
    if (Array.isArray(list)) {
      for (const it of list) {
        if (typeof it === 'string') result.push({ image: it });
        else if (it && (it.image || it.url || it.src)) result.push({ image: it.image || it.url || it.src });
      }
    }
    const single = (floorplanItem?.image_url as string | undefined) || (floorplanItem?.metadata?.image_url as string | undefined);
    if (!result.length && single) result.push({ image: single });
    return result;
  })();

  // Typical Floorplan (gallery-style with single-image fallback)
  const typicalItem = getContentItem('residences', `${unitKey}-typical-floorplan`);
  const typicalFlat = (() => {
    const result: { image: string }[] = [];
    const list = typicalItem?.metadata?.images;
    if (Array.isArray(list)) {
      for (const it of list) {
        if (typeof it === 'string') result.push({ image: it });
        else if (it && (it.image || it.url || it.src)) result.push({ image: it.image || it.url || it.src });
      }
    }
    const single = (typicalItem?.image_url as string | undefined) || (typicalItem?.metadata?.image_url as string | undefined);
    if (!result.length && single) result.push({ image: single });
    return result;
  })();

  const normalizeSlides = (item: any) => {
    const imgs = item?.metadata?.images;
    if (!Array.isArray(imgs) || imgs.length === 0) return [] as { image: string; text: string }[];
    return imgs
      .map((it: any) => {
        if (typeof it === 'string') return { image: it, text: '' };
        const image = it?.image || it?.url || it?.src || '';
        const text = it?.text || it?.caption || '';
        return image ? { image, text } : null;
      })
      .filter(Boolean) as { image: string; text: string }[];
  };

  const slides1 = normalizeSlides(sec1);
  const slides2 = normalizeSlides(sec2);
  const slides3 = normalizeSlides(sec3);
  const slides4 = normalizeSlides(sec4);

  useEffect(() => {
    // reset sliders when tab changes and sync floorplans key
    setCurrentSlide1(0);
    setCurrentSlide2(0);
    setCurrentSlide3(0);
    setCurrentSlide4(0);
    setActiveUnitTabState(unitKey);
  }, [activeUnitTab, unitKey]);

  // Brochure URL (admin-managed via contact-icons pdf_url; fallback to default)
  const brochureIcons = getContentItem('home', 'contact-icons') || getContentItem('residences', 'contact-icons');
  const brochureUrl = brochureIcons?.metadata?.pdf_url || '/catalog.pdf';

  // After successful form submit, trigger brochure download automatically
  useEffect(() => {
    if (submitStatus === 'success' && brochureUrl) {
      const link = document.createElement('a');
      link.href = brochureUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [submitStatus, brochureUrl]);

  // Gallery (admin-managed per unit)
  const galleryItem = getContentItem('residences', `${unitKey}-gallery`);
  const galleryFlat = (() => {
    const result: { image: string; category: string }[] = [];
    const list = galleryItem?.metadata?.images;
    if (Array.isArray(list)) {
      for (const it of list) {
        if (typeof it === 'string') result.push({ image: it, category: '' });
        else if (it && (it.image || it.url || it.src)) result.push({ image: it.image || it.url || it.src, category: it.category || '' });
      }
    }
    return result;
  })();

  // 3D View (admin-managed per unit, gallery style)
  const view3DItem = getContentItem('residences', `${unitKey}-3dview`);
  const view3DFlat = (() => {
    const result: { image: string; category: string }[] = [];
    const list = view3DItem?.metadata?.images;
    if (Array.isArray(list)) {
      for (const it of list) {
        if (typeof it === 'string') result.push({ image: it, category: '' });
        else if (it && (it.image || it.url || it.src)) result.push({ image: it.image || it.url || it.src, category: it.category || '' });
      }
    }
    return result;
  })();
  
  const nextView3DSlide = () => {
    const total = view3DFlat.length;
    if (!total) return;
    setCurrentView3DSlide((prev) => (prev + 1) % total);
  };

  const prevView3DSlide = () => {
    const total = view3DFlat.length;
    if (!total) return;
    setCurrentView3DSlide((prev) => (prev - 1 + total) % total);
  };

  const nextFloorplanSlide = () => {
    const total = floorplanFlat.length;
    if (!total) return;
    setCurrentFloorplanSlide((prev) => (prev + 1) % total);
  };

  const prevFloorplanSlide = () => {
    const total = floorplanFlat.length;
    if (!total) return;
    setCurrentFloorplanSlide((prev) => (prev - 1 + total) % total);
  };

  const nextTypicalSlide = () => {
    const total = typicalFlat.length;
    if (!total) return;
    setCurrentTypicalSlide((prev) => (prev + 1) % total);
  };

  const prevTypicalSlide = () => {
    const total = typicalFlat.length;
    if (!total) return;
    setCurrentTypicalSlide((prev) => (prev - 1 + total) % total);
  };

useEffect(() => {
    const interval = setInterval(() => {
      if (slides1.length) setCurrentSlide1((p) => (p + 1) % slides1.length);
      if (slides2.length) setCurrentSlide2((p) => (p + 1) % slides2.length);
      if (slides3.length) setCurrentSlide3((p) => (p + 1) % slides3.length);
      if (slides4.length) setCurrentSlide4((p) => (p + 1) % slides4.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [slides1.length, slides2.length, slides3.length, slides4.length]);

  const nextGallerySlide = () => {
    const total = galleryFlat.length;
    if (!total) return;
    setCurrentGallerySlide((prev) => (prev + 1) % total);
  };

  const prevGallerySlide = () => {
    const total = galleryFlat.length;
    if (!total) return;
    setCurrentGallerySlide((prev) => (prev - 1 + total) % total);
  };

  const getCurrentGalleryImage = () => {
    const total = galleryFlat.length;
    if (!total) return { image: '', category: '' };
    const idx = ((currentGallerySlide % total) + total) % total;
    return galleryFlat[idx];
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await api.post('/forms', {
        form_type: 'register_interest',
        name: formData.name,
        mobile: `${countryCode}${formData.mobile}`,
        email: formData.email,
        unit_type: formData.unitType,
        country_code: countryCode,
        status: 'new'
      });

      setSubmitStatus('success');
      setFormData({ name: '', mobile: '', email: '', unitType: '' });
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
const getTabLabel = (tabId: string) => {
    const tab = unitTabs.find(t => t.id === tabId);
    return tab ? tab.label : '';
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        {/* Background Media */}
        <div className="absolute inset-0">
          {heroVideoUrl ? (
            <video
              src={heroVideoUrl}
              poster={heroPosterUrl}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover object-top"
            />
          ) : heroImageUrl ? (
            <img
              src={heroImageUrl}
              alt="Residences"
              className="w-full h-full object-cover object-top"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center h-full text-center px-4">
          <h1 className="text-5xl lg:text-7xl font-light tracking-wide text-white mb-4">
            {residencesHeroTitleLine1} {residencesHeroTitleBold && (<span className="font-bold">{residencesHeroTitleBold}</span>)}
          </h1>
          {residencesHeroSubtitle?.trim() && (
            <p className="max-w-3xl mx-auto text-base md:text-lg text-white/80 mb-6">{residencesHeroSubtitle}</p>
          )}
          {residencesHeroButtonText?.trim() && (
            residencesHeroButtonUrl && residencesHeroButtonUrl.trim() !== '#' ? (
              <a href={residencesHeroButtonUrl} className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap">
                {residencesHeroButtonText.trim()}
              </a>
            ) : (
              <button className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap">
                {residencesHeroButtonText.trim()}
              </button>
            )
          )}
        </div>
      </section>

      {/* Unit Tabs Bar - below hero */}
      <section className="bg-black border-t border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center gap-8 py-4 text-white">
            <button
              onClick={() => setActiveUnitTab('Studio')}
              className={`text-base md:text-lg tracking-wider transition-all duration-300 cursor-pointer hover:text-[#C5A46D] ${
                activeUnitTab === 'Studio'
                  ? 'text-white border-b-2 border-white pb-1'
                  : 'text-white/70'
              }`}
            >
              Studio
            </button>
            <span className="text-white/40">|</span>
            <button
              onClick={() => setActiveUnitTab('1 Bedroom')}
              className={`text-base md:text-lg tracking-wider transition-all duration-300 cursor-pointer hover:text-[#C5A46D] ${
                activeUnitTab === '1 Bedroom'
                  ? 'text-white border-b-2 border-white pb-1'
                  : 'text-white/70'
              }`}
            >
              1 Bedroom
            </button>
            <span className="text-white/40">|</span>
            <button
              onClick={() => setActiveUnitTab('2 Bedroom')}
              className={`text-base md:text-lg tracking-wider transition-all duration-300 cursor-pointer hover:text-[#C5A46D] ${
                activeUnitTab === '2 Bedroom'
                  ? 'text-white border-b-2 border-white pb-1'
                  : 'text-white/70'
              }`}
            >
              2 Bedroom
            </button>
            <span className="text-white/40">|</span>
            <button
              onClick={() => setActiveUnitTab('Penthouse')}
              className={`text-base md:text-lg tracking-wider transition-all duration-300 cursor-pointer hover:text-[#C5A46D] ${
                activeUnitTab === 'Penthouse'
                  ? 'text-white border-b-2 border-white pb-1'
                  : 'text-white/70'
              }`}
            >
              Penthouse
            </button>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="gold-bg py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 text-sm text-black">
            <a href="/" className="text-black/80 hover:text-black hover:underline underline-offset-4 cursor-pointer">Home</a>
            <span className="text-black/60">/</span>
            <a href="/residences" className="text-black/80 hover:text-black hover:underline underline-offset-4 cursor-pointer">Residences</a>
            <span className="text-black/60">/</span>
            <span className="text-black font-medium">{activeUnitTab}</span>
          </div>
        </div>
      </nav>

      {/* WHY SECTION (Admin-managed) */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-light mb-8">
            {introTitle} <span className="font-bold">{introHighlight}</span>
          </h2>
          <p className="text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            {introDescription}
          </p>
          {introButtonUrl ? (
            <a
              href={introButtonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap inline-block"
            >
              {introButtonText}
            </a>
          ) : (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {introButtonText}
            </button>
          )}
        </div>
      </section>

      {/* Section 1 (caption left, image right) */}
      {slides1.length > 0 && (
        <section className="py-20 gold-bg">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl text-black font-semibold mb-6 whitespace-pre-line">{slides1[currentSlide1]?.text}</div>
                <div className="flex justify-center lg:justify-start space-x-2">
                  {slides1.map((_: unknown, index: number) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full ${index === currentSlide1 ? 'bg-black' : 'bg-black/40'}`}
                      onClick={() => setCurrentSlide1(index)}
                    />
                  ))}
                </div>
              </div>
              <div className="relative">
                <img src={slides1[currentSlide1]?.image} alt="Section image" className="w-full h-96 object-cover rounded-lg" />
                {slides1.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide1((prev) => (prev - 1 + slides1.length) % slides1.length)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <button
                      onClick={() => setCurrentSlide1((prev) => (prev + 1) % slides1.length)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-right-line text-xl"></i>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
      
      {slides2.length > 0 && (
        <section className="py-20 gold-bg">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative lg:order-1">
                <img src={slides2[currentSlide2]?.image} alt="Section image" className="w-full h-96 object-cover rounded-lg" />
                {slides2.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide2((prev) => (prev - 1 + slides2.length) % slides2.length)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <button
                      onClick={() => setCurrentSlide2((prev) => (prev + 1) % slides2.length)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-right-line text-xl"></i>
                    </button>
                  </>
                )}
              </div>
              <div className="text-center lg:text-left lg:order-2">
                <div className="text-3xl md:text-4xl text-black font-semibold mb-6 whitespace-pre-line">{slides2[currentSlide2]?.text}</div>
                <div className="flex justify-center lg:justify-start space-x-2">
                  {slides2.map((_: unknown, index: number) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full ${index === currentSlide2 ? 'bg-black' : 'bg-black/40'}`}
                      onClick={() => setCurrentSlide2(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {slides3.length > 0 && (
        <section className="py-20 gold-bg">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl text-black font-semibold mb-6 whitespace-pre-line">{slides3[currentSlide3]?.text}</div>
                <div className="flex justify-center lg:justify-start space-x-2">
                  {slides3.map((_: unknown, index: number) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full ${index === currentSlide3 ? 'bg-black' : 'bg-black/40'}`}
                      onClick={() => setCurrentSlide3(index)}
                    />
                  ))}
                </div>
              </div>
              <div className="relative">
                <img src={slides3[currentSlide3]?.image} alt="Section image" className="w-full h-96 object-cover rounded-lg" />
                {slides3.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide3((prev) => (prev - 1 + slides3.length) % slides3.length)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <button
                      onClick={() => setCurrentSlide3((prev) => (prev + 1) % slides3.length)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-right-line text-xl"></i>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Section 4 (caption left, image right) */}
      {slides4.length > 0 && (
        <section className="py-20 gold-bg">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img src={slides4[currentSlide4]?.image} alt="Section image" className="w-full h-96 object-cover rounded-lg" />
                {slides4.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide4((prev) => (prev - 1 + slides4.length) % slides4.length)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <button
                      onClick={() => setCurrentSlide4((prev) => (prev + 1) % slides4.length)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <i className="ri-arrow-right-line text-xl"></i>
                    </button>
                  </>
                )}
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl md:text-4xl text-black font-semibold mb-6 whitespace-pre-line">{slides4[currentSlide4]?.text}</div>
                <div className="flex justify-center lg:justify-start space-x-2">
                  {slides4.map((_: unknown, index: number) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full ${index === currentSlide4 ? 'bg-black' : 'bg-black/40'}`}
                      onClick={() => setCurrentSlide4(index)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Register Interest */}
      <section className="py-12 text-center">
        <button 
          onClick={() => setIsModalOpen(true)}
          className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          REGISTER YOUR INTEREST
        </button>
      </section>

      {/* Project Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-light text-center mb-6 text-white/90">
            Discover the full range of living options thoughtfully designed at Golden Tower.
          </h2>
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div className="flex space-x-8 border-b border-gray-700">
              {[
                { id: 'floorplan', label: 'Floor Plan' },
                { id: 'typical', label: 'Typical Floorplan' },
                { id: '3dview', label: '3D View' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveProjectTab(tab.id as ProjectTab)}
                  className={`pb-4 px-6 text-sm tracking-wider transition-all duration-300 ${
                    activeProjectTab === tab.id
                      ? 'text-white border-b-2 border-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Removed Fit/Fill toggle as requested */}

          {/* Tab Content */}
          <div className="min-h-96">
            {activeProjectTab === 'floorplan' && (
              <div className="w-full rounded-lg bg-gray-900 flex flex-col items-center justify-center text-center py-16 px-6">
                <p className="text-gray-300 max-w-2xl mb-8">
                  To explore our residence types and detailed layouts, please download the brochure.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Download Catalog
                </button>
              </div>
            )}

            {activeProjectTab === '3dview' && (
              view3DFlat.length > 0 ? (
                <div className="relative max-w-6xl mx-auto">
                  <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
                    <img
                      src={view3DFlat[((currentView3DSlide % view3DFlat.length)+view3DFlat.length)%view3DFlat.length].image}
                      alt={view3DFlat[((currentView3DSlide % view3DFlat.length)+view3DFlat.length)%view3DFlat.length].category}
                      className="absolute inset-0 w-full h-full object-contain"
                    />
                    {view3DFlat[((currentView3DSlide % view3DFlat.length)+view3DFlat.length)%view3DFlat.length].category && (
                      <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-xs tracking-widest uppercase">
                        {view3DFlat[((currentView3DSlide % view3DFlat.length)+view3DFlat.length)%view3DFlat.length].category}
                      </div>
                    )}
                  </div>
                  {view3DFlat.length > 1 && (
                    <>
                      <button
                        onClick={prevView3DSlide}
                        className="absolute left-4 sm:left-6 md:left-8 lg:left-10 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                      >
                        <i className="ri-arrow-left-line text-xl"></i>
                      </button>
                      <button
                        onClick={nextView3DSlide}
                        className="absolute right-16 sm:right-20 md:right-24 lg:right-28 xl:right-32 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                      >
                        <i className="ri-arrow-right-line text-xl"></i>
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full h-[600px] rounded-lg bg-gray-800 flex items-center justify-center text-gray-400">
                  3D VIEW images are not set
                </div>
              )
            )}

            {activeProjectTab === 'typical' && (
              <div className="w-full rounded-lg bg-gray-900 flex flex-col items-center justify-center text-center py-16 px-6">
                <p className="text-gray-300 max-w-2xl mb-8">
                  To explore our residence types and detailed layouts, please download the brochure.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Download Catalog
                </button>
              </div>
            )}
            
          </div>
        </div>
      </section>

      {/* 360 Virtual Tour */}
      {showVirtualTour && (
        <section className="py-20 gold-bg">
          <div className="container mx-auto px-4">
            <div className="h-96 rounded-lg overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                allow="xr-spatial-tracking; gyroscope; accelerometer"
                allowFullScreen
                scrolling="no"
                title="360-degree virtual tour of the space"
                src="https://kuula.co/share/collection/71RXS?logo=-1&info=0&fs=0&vr=0&sd=1&gyro=1&autop=0&thumbs=-1&keys=0"
                className="border-0"
              />
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-light text-center mb-10">
            Gallery
          </h2>
          {galleryFlat.length > 0 ? (
            <div className="relative max-w-6xl mx-auto">
              <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
                <img
                  src={getCurrentGalleryImage().image}
                  alt={getCurrentGalleryImage().category}
                  className="absolute inset-0 w-full h-full object-contain"
                />
                {getCurrentGalleryImage().category && (
                  <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-xs tracking-widest uppercase">
                    {getCurrentGalleryImage().category}
                  </div>
                )}
              </div>
              {galleryFlat.length > 1 && (
                <>
                  <button
                    onClick={prevGallerySlide}
                    className="absolute left-4 sm:left-6 md:left-8 lg:left-10 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <button
                    onClick={nextGallerySlide}
                    className="absolute right-16 sm:right-20 md:right-24 lg:right-28 xl:right-32 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                  >
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="w-full h-[600px] rounded-lg bg-gray-800 flex items-center justify-center text-gray-400">
              GALLERY images are not set
            </div>
          )}
        </div>
      </section>

      {/* Contact Section (amenities-style) */}
      <ContactSection page="residences" showFloatingIcons={true} />

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-black border border-gray-700 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-xl font-light text-white">REGISTER NOW FOR <span className="font-bold">MORE DETAILS</span></h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              <HubSpotForm />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}